import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { GGDialog, GGDialogTitle } from './StyledDialogs';
import BattlemapsCard from './BattlemapsCard';
import { scrollbarStyles } from './StyledComponents';
import {
  BATTLEMAP_CATALOG,
  BATTLEMAP_ENVIRONMENTS,
  getBattlemapById,
} from '../../services/battlemapService';

const BATTLEMAP_NONE = BATTLEMAP_CATALOG.find((b) => b.id === 'none');
const CATALOG_WITHOUT_NONE = BATTLEMAP_CATALOG.filter((b) => b.id !== 'none');

function catalogEntryMatches(entry, q, envFilter) {
  const matchEnv = envFilter === 'all' || entry.environment === envFilter;
  if (!matchEnv) return false;
  if (!q) return true;
  const ql = q.toLowerCase();
  if (entry.id?.toLowerCase().includes(ql) || entry.name?.toLowerCase().includes(ql)) {
    return true;
  }
  if (entry.kind === 'group') {
    return entry.variations.some(
      (v) =>
        v.id.toLowerCase().includes(ql) ||
        v.name.toLowerCase().includes(ql) ||
        (v.label && v.label.toLowerCase().includes(ql))
    );
  }
  return false;
}

function moveSelectedBattlemapToTop(filtered, battlemapId) {
  const selectedMap = filtered.find(map => {
    if (map.id === battlemapId) {
      return map;
    }else if (map.kind === 'group') {
      return map.variations.find(variation => variation.id === battlemapId);
    }
  });
  console.log('selectedMap', selectedMap);
  if (selectedMap) {
    filtered.splice(filtered.indexOf(selectedMap), 1);
    filtered.unshift(selectedMap);
  }
  return filtered;
}

const MapEditorModal = ({
  open,
  onClose,
  onSave,
  isLoading,
  mode = 'create',
  initialData = null,
}) => {
  const [mapName, setMapName] = useState('');
  const [gridWidth, setGridWidth] = useState(20);
  const [gridHeight, setGridHeight] = useState(20);
  const [battlemapId, setBattlemapId] = useState('none');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [mapSearch, setMapSearch] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [variationPicker, setVariationPicker] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        setMapName(initialData.name || '');
        setGridWidth(initialData.gridWidth || 20);
        setGridHeight(initialData.gridHeight || 20);
        setBattlemapId(initialData.battlemapId || 'none');
      } else {
        setMapName('');
        setGridWidth(20);
        setGridHeight(20);
        setBattlemapId('none');
      }
      setHasAttemptedSubmit(false);
    } else {
      setMapSearch('');
      setEnvironmentFilter('all');
      setVariationPicker(null);
    }
  }, [open, mode, initialData]);

  const handleBattlemapSelect = useCallback((selectedBattlemapId) => {
    setBattlemapId(selectedBattlemapId);
    const selectedBattlemap = getBattlemapById(selectedBattlemapId);
    if (selectedBattlemap && selectedBattlemap.gridWidth > 0 && selectedBattlemap.gridHeight > 0) {
      setGridWidth(selectedBattlemap.gridWidth);
      setGridHeight(selectedBattlemap.gridHeight);
    }
  }, []);

  const pickVariation = useCallback(
    (id) => {
      handleBattlemapSelect(id);
      setVariationPicker(null);
    },
    [handleBattlemapSelect]
  );

  const filteredCatalog = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    
    let filtered = CATALOG_WITHOUT_NONE.filter((entry) =>
      catalogEntryMatches(entry, q, environmentFilter)
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Move the selected battlemap to the top of the list
    return moveSelectedBattlemapToTop(filtered, battlemapId);
  }, [battlemapId, mapSearch, environmentFilter]);

  const handleSave = () => {
    setHasAttemptedSubmit(true);
    if (!mapName.trim()) return;

    onSave({
      name: mapName.trim(),
      gridWidth: parseInt(gridWidth, 10) || 20,
      gridHeight: parseInt(gridHeight, 10) || 20,
      battlemapId,
    });
  };

  const isValid = mapName.trim().length > 0;

  const renderCatalogCard = (entry) => {
    let preview = entry.variations ? entry.variations[0] : entry;
    let isSelected = entry.id === battlemapId;
    let chipLabel = null;
    let isVariationPicker = false;

    if (entry.kind === 'group') {
      const selectedVariation = entry.variations.find((v) => v.id === battlemapId);
      isSelected = !!selectedVariation;
      preview = selectedVariation || entry.variations[0];
      chipLabel = `${entry.variations.length} variations`;
      isVariationPicker = true;
    }

    return (
      <Grid item xs={6} sm={4} md={3} key={entry.id}>
        <BattlemapsCard
          selected={isSelected}
          onClick={() => !isVariationPicker ? handleBattlemapSelect(entry.id) : setVariationPicker(entry)}
          thumbnail={preview.thumbnail}
          alt={entry.name}
          title={entry.name}
          subtitle={ isVariationPicker ? `Selected: ${preview.label}` : null}
          gridWidth={preview.gridWidth}
          gridHeight={preview.gridHeight}
          chipLabel={chipLabel}
        />
      </Grid>
    );
  };

  return (
    <>
      <GGDialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullHeight>
        <GGDialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {mode === 'create' ? 'Create New Map' : 'Edit Map'}
            <Typography
              variant="caption"
              sx={{
                mb: 1.5,
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            >
              Battle maps by{' '}
              <a
                href="https://2minutetabletop.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#DAA520', textDecoration: 'none' }}
              >
                2-Minute Tabletop
              </a>
            </Typography>
          </Box>
        </GGDialogTitle>
        <DialogContent sx={{ pt: 3, overflow: 'visible', width: '100%', boxSizing: 'border-box' }}>
          <Grid container spacing={2} sx={{ width: '100%', minWidth: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                label="Map Name"
                fullWidth
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                error={hasAttemptedSubmit && !mapName.trim()}
                helperText={hasAttemptedSubmit && !mapName.trim() ? 'Map name is required' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Grid Width"
                  type="number"
                  value={gridWidth}
                  onChange={(e) => setGridWidth(e.target.value)}
                  inputProps={{ min: 1, max: 100 }}
                  fullWidth
                />
                <TextField
                  label="Grid Height"
                  type="number"
                  value={gridHeight}
                  onChange={(e) => setGridHeight(e.target.value)}
                  inputProps={{ min: 1, max: 100 }}
                  fullWidth
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <Divider sx={{ my: 2 }}>Battlemaps</Divider>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  placeholder="Search by name or id…"
                  size="small"
                  fullWidth
                  value={mapSearch}
                  onChange={(e) => setMapSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                  <InputLabel id="battlemap-env-filter">Environment</InputLabel>
                  <Select
                    labelId="battlemap-env-filter"
                    label="Environment"
                    value={environmentFilter}
                    onChange={(e) => setEnvironmentFilter(e.target.value)}
                  >
                    <MenuItem value="all">All environments</MenuItem>
                    {BATTLEMAP_ENVIRONMENTS.map((env) => (
                      <MenuItem key={env.id} value={env.id}>
                        {env.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {BATTLEMAP_NONE && (
                <Box sx={{ mb: 2, maxWidth: { xs: '100%', sm: 220 } }}>
                  <BattlemapsCard
                    selected={battlemapId === 'none'}
                    onClick={() => handleBattlemapSelect('none')}
                    title={null}
                    previewHeight={60}
                    emptyStateText="No Battlemap"
                  />
                </Box>
              )}

              <Box
                sx={(theme) => ({
                  maxHeight: 'calc(80vh - 380px)',
                  minHeight: 260,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflowY: 'auto',
                  pr: 1,
                  boxSizing: 'border-box',
                  ...scrollbarStyles(theme),
                })}
              >
                {filteredCatalog.length === 0 ? (
                  <Typography sx={{ color: 'text.secondary', py: 2 }}>
                    No battlemaps match your search or filter.
                  </Typography>
                ) : (
                  <Grid container spacing={1.5}>
                    {filteredCatalog.map((entry) => renderCatalogCard(entry))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ fontFamily: 'MedievalSharp' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="outlined"
            disabled={!isValid || isLoading}
            sx={{ fontFamily: 'MedievalSharp' }}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : mode === 'create' ? (
              'Create Map'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </GGDialog>

      <GGDialog
        open={Boolean(variationPicker)}
        onClose={() => setVariationPicker(null)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <GGDialogTitle>
          <Box>
            {variationPicker?.title}
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, fontFamily: 'MedievalSharp', fontSize: '0.95rem', fontWeight: 400 }}
            >
              Choose a variation
            </Typography>
          </Box>
        </GGDialogTitle>
        <DialogContent
          sx={(theme) => ({
            pt: 2,
            pb: 2,
            bgcolor: theme.palette.background.light,
          })}
        >
          {variationPicker && (
            <Grid container spacing={1.5} sx={{ pt: 2 }}>
              {variationPicker.variations.map((v) => (
                <Grid item xs={6} sm={4} key={v.id}>
                  <BattlemapsCard
                    selected={battlemapId === v.id}
                    onClick={() => pickVariation(v.id)}
                    thumbnail={v.thumbnail}
                    alt={v.name}
                    title={v.label || v.name}
                    subtitle={v.name !== v.label && v.label ? v.name : null}
                    gridWidth={v.gridWidth}
                    gridHeight={v.gridHeight}
                    previewHeight={100}
                    titleFontSize=".95rem"
                    subtitleFontSize="0.65rem"
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={(theme) => ({
            px: 3,
            pb: 2,
            pt: 1.5,
            bgcolor: theme.palette.background.light,
            borderTop: `2px solid ${theme.palette.background.dark}`,
          })}
        >
          <Button onClick={() => setVariationPicker(null)} sx={{ fontFamily: 'MedievalSharp' }}>
            Cancel
          </Button>
        </DialogActions>
      </GGDialog>
    </>
  );
};

export default MapEditorModal;
